import { useMemo, useEffect } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FacilityOperationsApi from 'libs/api/facility/operationsApi';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import { getShops, getInactiveShops } from 'hooks/useShops';
import { useApi } from 'hooks/useApi';
import OperationsAdapter, { FormValuesAdapter } from './operations.adapter';
import moment from 'moment';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useFilter } from 'hooks/useFilter';

const api = new FacilityOperationsApi();

const statusColors = {
  status_expired: 'black',
  status_new: 'yellow',
  status_to_admin: 'yellow',
  status_confirmed: 'green',
  status_refused: 'red',
  status_disactive: 'red',
  status_step1: 'yellow',
};

const useOperations = (isMainPage = true, isEdit) => {
  let { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const pages = useRecoilValue(getPage);
  const { filterData } = useFilter();
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const {
    data,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    setLoading,
    singleItem,
    setUpdateInProcess,
    updateInProcess,
    setSingleItem,
    onDelete,
    handleApprove,
  } = useApi({
    resourceKey: 'facility_operations',
    singleResourceKey: 'facility_operation',
    api: api,
    isMainPage,
    adapter: OperationsAdapter,
  });

  const facilityTypes = useMemo(
    () => ({
      normal_service: t('Manutenzione ordinaria'),
      extraordinary_service: t('Manutenziona straordinaria'),
      restyling_of_the_store: t('Store Restyling'),
      fire_test_evacuation: t('Prova antincendio/evacuazione'),
      technical_surveys_of_factories: t('Verifiche tecniche impianti'),
      delivery_return_on_site: t('Consegna/riconsegna locali'),
    }),
    [t],
  );

  const statuses = useMemo(
    () => ({
      status_expired: t('Expired'),
      status_new: t('Nuovo'),
      status_to_admin: t('Escalate to admin'),
      status_confirmed: t('Confermato'),
      status_refused: t('Rifiutato'),
      status_disactive: t('Disattiva'),
      status_step1: t('Step1 (facility lower)'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'facility_type',
        sorter: () => {},
        render: (data) => facilityTypes[data],
      },
      {
        title: t('Data richiesta'),
        dataIndex: 'created_at',
        sorter: () => {},
      },

      {
        title: t('Data inizio evento'),
        dataIndex: 'date_from',
        sorter: () => {},
        render: (data) => moment(data, 'YYYY-MM-DD').format('DD MMMM'),
      },
      {
        title: t('Data fine evento'),
        dataIndex: 'date_to',
        sorter: () => {},
        render: (data) => moment(data, 'YYYY-MM-DD').format('DD MMMM'),
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => (
          <Badge color={statusColors[data]} text={statuses[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_approve ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {(row.can_update || row.can_approve) && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, facilityTypes, statuses, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        values: [
          { value: 1, title: 'test1' },
          { value: 2, title: 'test2' },
          { value: 3, title: 'test3' },
        ],
        keys: ['by_date_from', 'by_date_to'],
      },

      {
        title: t('Titolo'),
        type: 'select',
        name: 'by_facility_type',
        values: Object.entries(facilityTypes).map((i) => ({
          value: i[0],
          title: i[1],
        })),
      },
    ],
    [t, filterData, facilityTypes],
  );

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = FormValuesAdapter(values);
      if (!id) {
        await api.createOperations(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateOperations(id, formData);
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
          'facility_operation_additional.car_type': 'car_type',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_from':
                case 'date_to': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'DD MMMM'),
                  });
                  break;
                }
                case 'time_from':
                case 'time_to': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'HH:mm'),
                  });
                  break;
                }
                case 'company': {
                  fieldsValues.push({
                    name: 'company_id',
                    value: singleItem[key]?.id,
                  });
                  break;
                }
                case 'files': {
                  break;
                }
                case 'facility_operation_additional': {
                  for (const singleItemElementKey in singleItem[key]) {
                    fieldsValues.push({
                      name: singleItemElementKey,
                      value: singleItem[key][singleItemElementKey],
                    });
                  }
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  const handleDeleteFile = async (item) => {
    setUpdateInProcess(true);
    try {
      await api.deleteFile(singleItem.id, item.file.id);
      setSingleItem((s) => ({
        ...s,
        files: s.files.filter((i) => i.id !== item.file.id),
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    loading,
    onChangeTableFilter,
    form,
    updateInProcess,
    shops,
    inactiveShops,
    facilityTypes,
    statuses,
    handleDownloadExcel,
    handleCreateOrUpdate,
    onDelete,
    singleItem,
    handleDeleteFile,
    handleApprove,
  };
};

export default useOperations;
